import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Loader2, BarChart3 } from "lucide-react";
import KPICards from "@/components/demo/KPICards";
import ForecastChart from "@/components/demo/ForecastChart";
import CategoryBreakdown from "@/components/demo/CategoryBreakdown";
import RegionalBreakdown from "@/components/demo/RegionalBreakdown";
import StockoutAlerts from "@/components/demo/StockoutAlerts";
import MacroFactors from "@/components/demo/MacroFactors";
import RevenueTracker from "@/components/demo/RevenueTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("demo_token");
      const res = await axios.get(`${API}/demo/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); navigate("/demo"); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const handleLogout = () => { logout(); navigate("/demo"); };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const categories = ["All", ...new Set(data.products?.map(p => p.category) || [])];

  return (
    <div className="min-h-screen bg-slate-950" data-testid="demo-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-[1600px] mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-cyan-400" />
            <div>
              <h1 className="text-base font-bold text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Demand Forecasting Platform
              </h1>
              <p className="text-[10px] text-slate-500">Pharma Supply Chain Intelligence | BharatIntelliAI</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchDashboard} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-800 transition-all" data-testid="refresh-btn">
              <RefreshCw size={16} />
            </button>
            <span className="text-xs text-slate-500 hidden md:block">{user?.email}</span>
            <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-all" data-testid="logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">
        {/* Category Filter */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
              className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                activeCategory === cat
                  ? "bg-cyan-500 text-slate-950"
                  : "bg-slate-900 text-slate-400 border border-slate-800 hover:border-cyan-500/30"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <KPICards kpi={data.kpi} />

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <ForecastChart historical={data.monthly_trend} forecast={data.future_forecast} />
          </div>
          <RevenueTracker targets={data.revenue_targets} categories={data.category_summary} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CategoryBreakdown categories={data.category_summary} />
          <RegionalBreakdown regions={data.region_summary} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <StockoutAlerts alerts={data.stockout_alerts} />
          <MacroFactors factors={data.macro_factors} />
        </div>
      </main>
    </div>
  );
}
